<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { createEventDispatcher, tick } from 'svelte';
  import { api, type UploadResponse, type SkippedFile } from '../api';
  import { activeHouseholdStore } from '../stores/activeHouseholdStore';
  import { compressImage } from '../uploadCompression';
  import { FileUp, X, UploadCloud, AlertTriangle } from 'lucide-svelte';
  import Button from './ui/Button.svelte';
  import Spinner from './ui/Spinner.svelte';

  const dispatch = createEventDispatcher<{ uploaded: UploadResponse }>();

  type SkippedItem = { fileName: string; reason: SkippedFile['reason'] | 'unsupported_type' };

  let selected: File[] = [];
  let uploading = false;
  let compressing = false;
  let compressionProgress: { current: number; total: number } | null = null;
  let error: string | null = null;
  let dragOver = false;
  // Chunked-upload progress + partial-failure summary (#279).
  let progress: { current: number; total: number } | null = null;
  let skipped: SkippedItem[] = [];
  let pendingResponse: UploadResponse | null = null;

  // Locked while running and while the partial-failure hold is open (E6, E7).
  $: locked = uploading || compressing || pendingResponse !== null;

  // `accept` only filters the file dialog, not drops — mirror it for dropped files (E16).
  const isSupportedType = (file: File) =>
    file.type.startsWith('image/') || file.type === 'application/pdf';

  async function handleFiles(list: ArrayLike<File> | null | undefined) {
    if (uploading || compressing || pendingResponse || !list || list.length === 0) return;

    error = null;
    pendingResponse = null;
    const all = Array.from(list);
    selected = all.filter(isSupportedType);
    skipped = all
      .filter((f) => !isSupportedType(f))
      .map((f) => ({ fileName: f.name, reason: 'unsupported_type' as const }));

    if (selected.length === 0) {
      error = $_('pages.upload.all_chunks_failed');
      return;
    }

    try {
      compressing = true;
      await tick();
      const fileArray: File[] = [];
      for (let i = 0; i < selected.length; i++) {
        compressionProgress =
          selected.length > 1 ? { current: i + 1, total: selected.length } : null;
        await tick();
        fileArray.push(await compressImage(selected[i]));
      }
      compressing = false;
      compressionProgress = null;

      uploading = true;
      const { response, skipped: skippedFiles } = await api.upload.uploadDocumentsChunked(
        fileArray,
        { householdId: $activeHouseholdStore },
        (current, total) => {
          progress = total > 1 ? { current, total } : null;
        },
      );

      skipped = [...skipped, ...skippedFiles];

      if (response.documents.length === 0) {
        // Nothing made it through — surface the summary and accept new files again.
        error = $_('pages.upload.all_chunks_failed');
        return;
      }

      if (skipped.length > 0) {
        // Some files uploaded, some were skipped — let the user read the summary,
        // then proceed to the processing view via the button.
        pendingResponse = response;
      } else {
        dispatch('uploaded', response);
      }
    } catch (err) {
      error = err instanceof Error ? err.message : $_('pages.upload.error');
    } finally {
      compressing = false;
      compressionProgress = null;
      uploading = false;
      progress = null;
      selected = [];
    }
  }

  function handleChange(event: Event) {
    const input = event.currentTarget as HTMLInputElement;
    const list = input.files ? Array.from(input.files) : [];
    // Reset so selecting the same file again fires `change` again.
    input.value = '';
    handleFiles(list);
  }

  function continueToSession() {
    if (pendingResponse) dispatch('uploaded', pendingResponse);
  }

  function handleDrop(event: DragEvent) {
    event.preventDefault();
    dragOver = false;
    handleFiles(event.dataTransfer?.files);
  }

  function handleDragOver(event: DragEvent) {
    event.preventDefault();
    if (!locked) dragOver = true;
  }

  function handleDragLeave() {
    dragOver = false;
  }
</script>

<div class="bg-surface border border-border rounded-lg p-5">
  <!-- Drop Zone -->
  <div
    class="relative border-2 border-dashed rounded-xl py-12 px-6 text-center cursor-pointer transition-colors lg:min-h-[40vh] lg:flex lg:flex-col lg:items-center lg:justify-center
      {locked ? 'opacity-50 cursor-not-allowed' : ''} focus-within:ring-2 focus-within:ring-accent
      {dragOver
      ? 'border-accent bg-accent-subtle scale-[1.01]'
      : 'border-border hover:border-border-strong hover:bg-surface-hover'}"
    on:drop={handleDrop}
    on:dragover={handleDragOver}
    on:dragleave={handleDragLeave}
    aria-disabled={locked}
    role="group"
  >
    <div
      class="inline-flex items-center justify-center w-14 h-14 rounded-full bg-bg-alt mb-4 shadow-sm"
    >
      <UploadCloud
        class="w-7 h-7 text-text-muted {dragOver ? 'text-accent' : ''}"
        strokeWidth={1.5}
      />
    </div>
    <p class="text-base font-semibold text-text mb-1">{$_('pages.upload.drag_drop_text')}</p>
    <p class="text-sm text-text-muted max-w-sm mx-auto">{$_('pages.upload.hint')}</p>
    <input
      id="file-input"
      data-testid="upload-file-input"
      type="file"
      on:change={handleChange}
      disabled={locked}
      multiple
      accept="image/*,application/pdf"
      class="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:pointer-events-none"
    />
  </div>

  <!-- Progress (upload starts on selection); the live region stays mounted so changes are announced -->
  <div role="status" aria-live="polite">
    {#if uploading || compressing}
      <div
        class="mt-3 flex items-center gap-2 text-sm text-text-secondary"
        data-testid="upload-progress"
      >
        <Spinner size="sm" decorative />
        {#if compressing && compressionProgress}
          {$_('pages.upload.compressing_progress', {
            values: { current: compressionProgress.current, total: compressionProgress.total },
          })}
        {:else if compressing}
          {$_('pages.upload.compressing')}
        {:else if progress}
          {$_('pages.upload.uploading_chunk', {
            values: { current: progress.current, total: progress.total },
          })}
        {:else}
          {$_('pages.upload.uploading')}
        {/if}
      </div>
    {/if}
  </div>

  <!-- File List -->
  {#if selected.length > 0}
    <div class="mt-4 bg-bg-alt rounded-lg p-3">
      <h3 class="text-sm font-medium text-text-secondary mb-2">
        {$_('pages.upload.selected_files')} ({selected.length})
      </h3>
      <ul class="space-y-1 max-h-48 overflow-y-auto">
        {#each selected as file, i (i)}
          <li class="flex items-center justify-between py-1.5 px-2 rounded hover:bg-surface">
            <div class="flex items-center gap-2 min-w-0">
              <FileUp class="w-4 h-4 text-text-muted shrink-0" strokeWidth={1.5} />
              <span class="text-sm text-text truncate">{file.name}</span>
            </div>
            <span class="text-xs text-text-muted shrink-0 ml-2"
              >{(file.size / 1024 / 1024).toFixed(2)} MB</span
            >
          </li>
        {/each}
      </ul>
    </div>
  {/if}

  <!-- Error -->
  {#if error}
    <div
      role="alert"
      class="mt-3 flex items-center gap-2 px-3 py-2 bg-error/10 border border-error/30 rounded text-sm text-error"
    >
      <X class="w-4 h-4 shrink-0" strokeWidth={1.5} />
      {error}
    </div>
  {/if}

  <!-- Partial-failure summary: files that could not be uploaded (#279) -->
  {#if skipped.length > 0}
    <div
      class="mt-3 px-3 py-2 bg-warning/10 border border-warning/30 rounded text-sm"
      data-testid="upload-skipped-summary"
    >
      <div class="flex items-center gap-2 text-warning font-medium mb-1">
        <AlertTriangle class="w-4 h-4 shrink-0" strokeWidth={1.5} />
        {$_('pages.upload.skipped_summary', { values: { count: skipped.length } })}
      </div>
      <ul class="ml-6 list-disc text-text-secondary">
        {#each skipped as item, i (i)}
          <li>
            <span class="text-text">{item.fileName}</span>
            <span class="text-text-muted">
              — {item.reason === 'too_large'
                ? $_('pages.upload.skipped_too_large')
                : item.reason === 'unsupported_type'
                  ? $_('pages.upload.skipped_unsupported_type')
                  : $_('pages.upload.skipped_failed')}</span
            >
          </li>
        {/each}
      </ul>
    </div>
  {/if}

  {#if pendingResponse}
    <div class="mt-4 flex gap-2">
      <Button variant="primary" on:click={continueToSession} data-testid="upload-continue-button">
        {$_('pages.upload.continue_to_processing')}
      </Button>
    </div>
  {/if}
</div>
